import React, { useRef } from 'react';

import { useChatMixpanel } from '~/components/AiChat/hooks/useChatMixpanel';
import ChatIcon from '~/components/AiChat/SideBarDialog/icons/ChatIcon';
import { Text } from '~/components/core';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import { PencilIcon } from '~/components/icons';
import { formatShortDate, serverDateTimeToLocal } from '~/DateTimeUtils';
import { CLIVE_CHAT_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import colors from '~/theme/tailwind/colors';
import cn from '~/Utils/cn';

interface ChatHistoryItemProps {
  /** Reported as Mixpanel's `selected_chat_id` and `renamed_chat_id`. */
  analyticsId: string | number;
  createdAt: string;
  displayTitle: string;
  onClick?: () => void;
  onRenameClick?: () => void;
  showRename?: boolean;
}

export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({
  analyticsId,
  createdAt,
  displayTitle,
  onClick,
  onRenameClick,
  showRename = true,
}) => {
  const menuAnchorRef = useRef<HTMLDivElement>(null);
  const { trackChatEvent } = useChatMixpanel();
  const localCreatedAt = serverDateTimeToLocal(createdAt);

  const handleClick = () => {
    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_HISTORY_ITEM_SELECTED, { selected_chat_id: analyticsId });
    onClick?.();
  };

  const handleRenameClick = () => {
    trackChatEvent(CLIVE_CHAT_MIXPANEL_EVENTS.CHAT_RENAME_STARTED, { renamed_chat_id: analyticsId });
    onRenameClick?.();
  };

  const menuOptions = [
    {
      key: 'rename',
      onClick: handleRenameClick,
      node: (
        <div className="flex items-center gap-8">
          <PencilIcon />
          <span>Rename</span>
        </div>
      ),
    },
  ];

  return (
    <div
      className="group flex h-40 cursor-pointer items-center gap-12 rounded-lg px-12 hover:bg-blue-100"
      onClick={handleClick}
      role="button"
      tabIndex={0}
    >
      <ChatIcon size={20} iconColor={colors.blue[600]} />
      <div className="flex flex-1 items-center justify-between gap-8 py-4">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM} className="line-clamp-1">
            {displayTitle}
          </Text>
        </div>
        <div ref={menuAnchorRef} className="flex h-24 shrink-0 items-center">
          <Text
            variant={Text.VARIANTS.XS}
            weight={Text.WEIGHTS.REGULAR}
            className={cn('block', {
              'group-hover:hidden': showRename,
            })}
          >
            {formatShortDate(localCreatedAt)}
          </Text>
          {showRename ? (
            <div className="hidden pr-4 group-hover:block" onClick={(e) => e.stopPropagation()}>
              <ThreeDotsMenu options={menuOptions} ignorePermission stopPropagation anchorRef={menuAnchorRef} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
